/**
 * McpDetailView — full-width detail page for the MCP Server,
 * shown in the main content area when the MCP Server card is clicked
 * from the EnginePanel sidebar.
 *
 * Displays:
 *   - MCP Server overview (name, version, protocol version, transport)
 *   - Registered Tools (16 tools grouped by category)
 *   - Client activity and request logs
 *   - Configuration snippets for Claude Desktop / Cursor / etc.
 */

import type {
    McpClientSessionInfo,
    McpPromptMetadata,
    McpRequestLogEntry,
    McpResourceMetadata,
    McpToolMetadata,
} from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import { CollapsibleSidebar, JsonTree } from "@magic-md/plugin-sdk";
import {
    ArrowLeft,
    BookOpen,
    Braces,
    ChevronDown,
    ChevronRight,
    Circle,
    Database,
    FileSearch,
    Filter,
    List,
    Loader2,
    PanelLeftClose,
    PanelLeftOpen,
    Play,
    Power,
    PowerOff,
    Puzzle,
    Server,
    Trash2,
    Users,
    Wrench,
    Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { getPlatformService } from "../platform";
import { useEditorStore } from "../store/editor-store";
import {
    getThemeAccentStyle,
    type ThemeAccent,
} from "../theme/runtime/theme-accents";

/* ── Category icon mapping ── */
function getCategoryIcon(category: string, size = 11): React.ReactNode {
    switch (category) {
        case "Document":
            return <FileSearch size={size} />;
        case "Definition":
            return <Braces size={size} />;
        case "Execution":
            return <Play size={size} />;
        case "Content":
            return <List size={size} />;
        case "Plugin":
            return <Puzzle size={size} />;
        default:
            return <Wrench size={size} />;
    }
}

/* ── Main component ── */

export function McpDetailView() {
    const t = useT("editor");
    const setShowMcpDetail = useEditorStore((s) => s.setShowMcpDetail);

    return (
        <div className="flex flex-col h-full bg-background">
            {/* Top bar */}
            <div className="flex items-center gap-2 h-10 px-4 border-b border-border shrink-0 bg-muted/20">
                <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowMcpDetail(false)}
                >
                    <ArrowLeft size={14} />
                    <span>{t("common.back")}</span>
                </button>
                <div className="w-px h-4 bg-border" />
                <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Server size={12} />
                    MCP Server
                </span>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 min-h-0 overflow-auto">
                <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
                    {/* ── Overview Card ── */}
                    <OverviewSection />

                    {/* ── Client Activity + Request Logs ── */}
                    <ClientActivitySection />

                    {/* ── Tools ── */}
                    <ToolsSection />

                    {/* ── Resources ── */}
                    <ResourcesSection />

                    {/* ── Prompts ── */}
                    <PromptsSection />
                </div>
            </div>
        </div>
    );
}

/* ── Sub-sections ── */

function SectionHeader({
    icon,
    title,
    badge,
}: {
    icon: React.ReactNode;
    title: string;
    badge?: string | number;
}) {
    return (
        <div className="flex items-center gap-2 mb-3">
            <span className="text-muted-foreground">{icon}</span>
            <h2 className="text-sm font-semibold text-foreground">{title}</h2>
            {badge != null ? (
                <span className="text-[10px] font-medium text-muted-foreground bg-muted/40 rounded px-1.5 py-0.5 tabular-nums">
                    {badge}
                </span>
            ) : null}
        </div>
    );
}

function OverviewSection() {
    const t = useT("editor");
    const mcpStatus = useEditorStore((s) => s.mcpStatus);
    const mcpEndpoint = useEditorStore((s) => s.mcpEndpoint);
    const toolsCount = useEditorStore((s) => s.mcpTools.length);
    const resourcesCount = useEditorStore((s) => s.mcpResources.length);
    const promptsCount = useEditorStore((s) => s.mcpPrompts.length);
    const setMcpStatus = useEditorStore((s) => s.setMcpStatus);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isRunning = mcpStatus === "running";
    const isBusy = mcpStatus === "starting" || mcpStatus === "stopping";

    const handleToggle = useCallback(async () => {
        const engine = getPlatformService().engine;
        setLoading(true);
        setError(null);
        try {
            if (isRunning) {
                if (engine.mcpStop) {
                    await engine.mcpStop();
                    setMcpStatus("stopped");
                }
            } else {
                if (engine.mcpStart) {
                    setMcpStatus("starting");
                    const startResult = await engine.mcpStart();
                    // Fetch full status (including tools/prompts/resources metadata)
                    const full = await engine.mcpStatus?.();
                    if (full) {
                        setMcpStatus(
                            full.status,
                            full.endpoint,
                            full.port,
                            full.connectedClients,
                            full.clients,
                            full.tools,
                            full.prompts,
                            full.resources,
                            full.knownClients,
                        );
                    } else {
                        setMcpStatus("running", startResult.endpoint, startResult.port);
                    }
                }
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
            setMcpStatus("error");
        } finally {
            setLoading(false);
        }
    }, [isRunning, setMcpStatus]);

    const statusAccent = (
        {
            stopped: null,
            starting: "warning",
            running: "success",
            stopping: "warning",
            error: "error",
        } as const satisfies Record<string, ThemeAccent | null>
    )[mcpStatus];

    const statusLabel = (
        {
            stopped: t("mcpDetail.status.stopped"),
            starting: t("mcpDetail.status.starting"),
            running: t("mcpDetail.status.running"),
            stopping: t("mcpDetail.status.stopping"),
            error: t("mcpDetail.status.error"),
        } as Record<string, string>
    )[mcpStatus];

    return (
        <div>
            <SectionHeader icon={<Server size={14} />} title={t("mcpDetail.overview")} />
            <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Server size={20} className="text-primary" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-sm font-semibold text-foreground">magic-md</h3>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                            <span className="rounded bg-muted/40 px-1.5 py-0.5 font-mono">
                                v0.2.1
                            </span>
                            <span className="rounded bg-muted/40 px-1.5 py-0.5">
                                MCP 2024-11-05
                            </span>
                            <span className="rounded bg-muted/40 px-1.5 py-0.5">
                                Streamable HTTP
                            </span>
                            <span className="rounded bg-muted/40 px-1.5 py-0.5 tabular-nums">
                                {t("mcpDetail.info.tools")} {toolsCount || "—"}
                            </span>
                            <span className="rounded bg-muted/40 px-1.5 py-0.5 tabular-nums">
                                {t("mcpDetail.info.resources")} {resourcesCount || "—"}
                            </span>
                            <span className="rounded bg-muted/40 px-1.5 py-0.5 tabular-nums">
                                {t("mcpDetail.info.prompts")} {promptsCount || "—"}
                            </span>
                        </div>
                    </div>
                    {/* Start / Stop button */}
                    <button
                        type="button"
                        disabled={isBusy || loading}
                        onClick={handleToggle}
                        className={cn(
                            "theme-accent-button flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                            isRunning
                                ? ""
                                : "",
                            (isBusy || loading) && "opacity-50 cursor-not-allowed",
                        )}
                        style={getThemeAccentStyle(isRunning ? "error" : "success")}
                    >
                        {isBusy || loading ? (
                            <Loader2 size={12} className="animate-spin" />
                        ) : isRunning ? (
                            <PowerOff size={12} />
                        ) : (
                            <Power size={12} />
                        )}
                        {isRunning ? t("mcpDetail.status.stop") : t("mcpDetail.status.start")}
                    </button>
                </div>

                {/* Status + Endpoint */}
                <div className="flex items-center gap-2 text-xs">
                    <Circle
                        size={8}
                        className={cn(
                            "fill-current",
                            statusAccent ? "theme-accent-text" : "text-muted-foreground/50",
                        )}
                        style={statusAccent ? getThemeAccentStyle(statusAccent) : undefined}
                    />
                    <span
                        className={cn(
                            "font-medium",
                            statusAccent ? "theme-accent-text" : "text-muted-foreground/50",
                        )}
                        style={statusAccent ? getThemeAccentStyle(statusAccent) : undefined}
                    >
                        {statusLabel}
                    </span>
                    {mcpEndpoint && isRunning ? (
                        <span className="font-mono text-[11px] text-muted-foreground bg-muted/40 rounded px-2 py-0.5 select-all">
                            {mcpEndpoint}
                        </span>
                    ) : null}
                </div>

                {error ? (
                    <p
                        className="theme-accent-banner rounded px-2 py-1.5 text-[11px]"
                        style={getThemeAccentStyle("error")}
                    >
                        {error}
                    </p>
                ) : null}
            </div>
        </div>
    );
}

type LogStatusFilter = "all" | "pending" | "success" | "error";
type ClientStatusFilter = "all" | "connected" | "disconnected";

const ALL_CLIENTS_ID = "__all__";
const STATELESS_CLIENT_ID = "__stateless__";

interface ClientActivityItem {
    id: string;
    sessionId: string | null;
    name: string;
    version?: string;
    connected: boolean;
    connectedAt?: number;
    disconnectedAt?: number;
    lastSeenAt?: number;
    requestCount: number;
    errorCount: number;
    synthetic?: boolean;
}

function ClientActivitySection() {
    const t = useT("editor");
    const mcpStatus = useEditorStore((s) => s.mcpStatus);
    const connectedClients = useEditorStore((s) => s.mcpClientSessions);
    const knownClients = useEditorStore((s) => s.mcpKnownClientSessions);
    const logs = useEditorStore((s) => s.mcpRequestLogs);
    const setMcpStatus = useEditorStore((s) => s.setMcpStatus);
    const pushMcpRequestLog = useEditorStore((s) => s.pushMcpRequestLog);
    const [selectedClientId, setSelectedClientId] = useState(ALL_CLIENTS_ID);
    const [clientFilter, setClientFilter] = useState<ClientStatusFilter>("all");
    const [clientQuery, setClientQuery] = useState("");
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const isRunning = mcpStatus === "running";

    useEffect(() => {
        let cancelled = false;
        const engine = getPlatformService().engine;
        const refresh = async () => {
            try {
                const result = await engine.mcpStatus?.();
                if (!cancelled && result) {
                    setMcpStatus(
                        result.status,
                        result.endpoint,
                        result.port,
                        result.connectedClients,
                        result.clients,
                        result.tools,
                        result.prompts,
                        result.resources,
                        result.knownClients,
                    );
                }
            } catch {
                // The detail view is still useful with locally streamed logs.
            }

            try {
                const result = await engine.mcpLogs?.({ limit: 500 });
                if (!cancelled && result) {
                    for (const log of result.logs) {
                        pushMcpRequestLog(log);
                    }
                }
            } catch {
                // Some connector modes may not expose historical MCP logs.
            }
        };

        void refresh();
        return () => {
            cancelled = true;
        };
    }, [pushMcpRequestLog, setMcpStatus]);

    const clients = useMemo(
        () => buildClientActivityItems(knownClients, connectedClients, logs, t),
        [knownClients, connectedClients, logs, t],
    );

    const filteredClients = useMemo(() => {
        const query = clientQuery.trim().toLowerCase();
        return clients.filter((client) => {
            if (clientFilter === "connected" && !client.connected) return false;
            if (clientFilter === "disconnected" && client.connected) return false;
            if (!query) return true;
            return [
                client.name,
                client.version,
                client.sessionId,
            ].some((value) => value?.toLowerCase().includes(query));
        });
    }, [clientFilter, clientQuery, clients]);

    const selectedClient = useMemo(
        () => clients.find((client) => client.id === selectedClientId) ?? null,
        [clients, selectedClientId],
    );

    return (
        <div>
            <SectionHeader
                icon={<Users size={14} />}
                title={t("mcpDetail.clients.title")}
                badge={clients.length}
            />

            <div className="rounded-lg border border-border bg-card overflow-hidden">
                <CollapsibleSidebar
                    collapsed={sidebarCollapsed}
                    storageKey="mcp-detail/client-sidebar"
                    defaultWidth={300}
                    minWidth={240}
                    maxWidth={420}
                    minMainWidth={420}
                    collapsedWidth={42}
                    className="h-[620px] min-h-[420px] min-w-0 overflow-hidden"
                    sidebarClassName="border-r border-border/40 bg-muted/10"
                    mainClassName="h-full min-h-0 min-w-0 bg-card"
                    collapsedSidebar={
                        <button
                            type="button"
                            className="flex h-full w-full items-start justify-center pt-3 text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                            onClick={() => setSidebarCollapsed(false)}
                            title={t("common.expand")}
                        >
                            <PanelLeftOpen size={14} />
                        </button>
                    }
                    sidebar={
                        <aside className="flex h-full min-h-0 flex-col">
                            <div className="border-b border-border/40 p-3 space-y-2">
                                <div className="flex items-center gap-2">
                                    <Users size={12} className="text-muted-foreground shrink-0" />
                                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground/80">
                                        {t("mcpDetail.clients.title")}
                                    </span>
                                    <button
                                        type="button"
                                        className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                        onClick={() => setSidebarCollapsed(true)}
                                        title={t("common.collapse")}
                                    >
                                        <PanelLeftClose size={12} />
                                    </button>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Filter size={11} className="text-muted-foreground/50 shrink-0" />
                                    <input
                                        type="text"
                                        placeholder={t("mcpDetail.clients.filterPlaceholder")}
                                        value={clientQuery}
                                        onChange={(e) => setClientQuery(e.target.value)}
                                        className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/40 outline-none"
                                    />
                                </div>
                                <div className="grid grid-cols-3 rounded-md border border-border/50 overflow-hidden text-[10px]">
                                    {(["all", "connected", "disconnected"] as ClientStatusFilter[]).map(
                                        (filter) => (
                                            <button
                                                key={filter}
                                                type="button"
                                                className={cn(
                                                    "px-2 py-1 transition-colors",
                                                    clientFilter === filter
                                                        ? "bg-primary/10 text-primary font-medium"
                                                        : "text-muted-foreground hover:text-foreground hover:bg-muted/30",
                                                )}
                                                onClick={() => setClientFilter(filter)}
                                            >
                                                {clientFilterLabel(filter, t)}
                                            </button>
                                        ),
                                    )}
                                </div>
                            </div>

                            <div className="min-h-0 flex-1 overflow-auto divide-y divide-border/20">
                                <button
                                    type="button"
                                    className={cn(
                                        "flex items-center gap-2 w-full px-3 py-2.5 text-left transition-colors",
                                        selectedClientId === ALL_CLIENTS_ID
                                            ? "bg-primary/10 text-foreground"
                                            : "hover:bg-muted/20 text-muted-foreground",
                                    )}
                                    onClick={() => setSelectedClientId(ALL_CLIENTS_ID)}
                                >
                                    <List size={12} className="shrink-0" />
                                    <span className="flex-1 text-xs font-medium">
                                        {t("mcpDetail.clients.all")}
                                    </span>
                                    <span className="text-[10px] font-mono tabular-nums opacity-60">
                                        {logs.length}
                                    </span>
                                </button>

                                {filteredClients.length === 0 ? (
                                    <div className="px-3 py-6 text-center">
                                        <p className="text-xs text-muted-foreground/50">
                                            {isRunning || logs.length > 0
                                                ? t("mcpDetail.clients.empty")
                                                : t("mcpDetail.clients.startHint")}
                                        </p>
                                    </div>
                                ) : (
                                    filteredClients.map((client) => (
                                        <ClientActivityRow
                                            key={client.id}
                                            client={client}
                                            selected={selectedClientId === client.id}
                                            onSelect={() => setSelectedClientId(client.id)}
                                        />
                                    ))
                                )}
                            </div>
                        </aside>
                    }
                >
                    <RequestLogPanel
                        logs={logs}
                        selectedClientId={selectedClientId}
                        selectedClient={selectedClient}
                    />
                </CollapsibleSidebar>
            </div>
        </div>
    );
}

function buildClientActivityItems(
    knownClients: McpClientSessionInfo[],
    connectedClients: McpClientSessionInfo[],
    logs: McpRequestLogEntry[],
    t: ReturnType<typeof useT>,
): ClientActivityItem[] {
    const connectedIds = new Set(connectedClients.map((client) => client.sessionId));
    const logStats = new Map<
        string,
        { requestCount: number; errorCount: number; firstSeenAt: number; lastSeenAt: number }
    >();

    for (const log of logs) {
        const id = log.sessionId ?? STATELESS_CLIENT_ID;
        const existing = logStats.get(id);
        if (!existing) {
            logStats.set(id, {
                requestCount: 1,
                errorCount: log.status === "error" ? 1 : 0,
                firstSeenAt: log.timestamp,
                lastSeenAt: log.timestamp,
            });
            continue;
        }
        existing.requestCount += 1;
        if (log.status === "error") existing.errorCount += 1;
        existing.firstSeenAt = Math.min(existing.firstSeenAt, log.timestamp);
        existing.lastSeenAt = Math.max(existing.lastSeenAt, log.timestamp);
    }

    const map = new Map<string, ClientActivityItem>();
    for (const client of [...knownClients, ...connectedClients]) {
        const stats = logStats.get(client.sessionId);
        const connected =
            client.status === "connected" ||
            (client.status == null && connectedIds.has(client.sessionId));
        const lastSeenAt = Math.max(
            client.lastSeenAt ?? 0,
            client.disconnectedAt ?? 0,
            client.connectedAt,
            stats?.lastSeenAt ?? 0,
        );
        map.set(client.sessionId, {
            id: client.sessionId,
            sessionId: client.sessionId,
            name: client.clientName ?? t("mcpDetail.clients.unknown"),
            version: client.clientVersion,
            connected,
            connectedAt: client.connectedAt,
            disconnectedAt: client.disconnectedAt,
            lastSeenAt,
            requestCount: Math.max(client.requestCount ?? 0, stats?.requestCount ?? 0),
            errorCount: Math.max(client.errorCount ?? 0, stats?.errorCount ?? 0),
        });
    }

    for (const [id, stats] of logStats) {
        if (map.has(id)) continue;
        map.set(id, {
            id,
            sessionId: id === STATELESS_CLIENT_ID ? null : id,
            name:
                id === STATELESS_CLIENT_ID
                    ? t("mcpDetail.clients.stateless")
                    : t("mcpDetail.clients.unknown"),
            connected: false,
            connectedAt: stats.firstSeenAt,
            lastSeenAt: stats.lastSeenAt,
            requestCount: stats.requestCount,
            errorCount: stats.errorCount,
            synthetic: true,
        });
    }

    return [...map.values()].sort((a, b) => {
        if (a.connected !== b.connected) return a.connected ? -1 : 1;
        return (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0);
    });
}

function ClientActivityRow({
    client,
    selected,
    onSelect,
}: {
    client: ClientActivityItem;
    selected: boolean;
    onSelect: () => void;
}) {
    const t = useT("editor");
    const sessionLabel = client.sessionId
        ? `${client.sessionId.slice(0, 8)}...${client.sessionId.slice(-4)}`
        : t("common.none");

    return (
        <button
            type="button"
            className={cn(
                "flex items-start gap-2.5 w-full px-3 py-2.5 text-left transition-colors",
                selected
                    ? "bg-primary/10 text-foreground"
                    : "hover:bg-muted/20 text-muted-foreground",
            )}
            onClick={onSelect}
        >
            <Circle
                size={8}
                className={cn(
                    "mt-1 shrink-0 fill-current",
                    client.connected ? "theme-accent-text" : "text-muted-foreground/40",
                )}
                style={client.connected ? getThemeAccentStyle("success") : undefined}
            />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate text-xs font-medium text-foreground/80">
                        {client.name}
                    </span>
                    {client.version ? (
                        <span className="shrink-0 text-[10px] font-mono text-muted-foreground/50">
                            v{client.version}
                        </span>
                    ) : null}
                </div>
                <div className="mt-0.5 flex items-center gap-2 min-w-0 text-[10px] text-muted-foreground/50">
                    <span className="font-mono truncate" title={client.sessionId ?? undefined}>
                        {sessionLabel}
                    </span>
                    <span className="shrink-0 tabular-nums">
                        {formatClientTime(client)}
                    </span>
                </div>
            </div>
            <div className="flex flex-col items-end gap-0.5 shrink-0">
                <span className="text-[10px] font-mono tabular-nums text-muted-foreground/60">
                    {client.requestCount}
                </span>
                {client.errorCount > 0 ? (
                    <span
                        className="theme-accent-text text-[10px] font-mono tabular-nums"
                        style={getThemeAccentStyle("error")}
                    >
                        {client.errorCount}
                    </span>
                ) : null}
            </div>
        </button>
    );
}

function clientFilterLabel(filter: ClientStatusFilter, t: ReturnType<typeof useT>): string {
    if (filter === "connected") return t("mcpDetail.clients.status.connected");
    if (filter === "disconnected") return t("mcpDetail.clients.status.disconnected");
    return t("common.all");
}

function formatClientTime(client: ClientActivityItem): string {
    const value = client.connected
        ? client.connectedAt
        : client.disconnectedAt ?? client.lastSeenAt ?? client.connectedAt;
    if (!value) return "";
    const date = new Date(value);
    return date.toLocaleTimeString("en-GB", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
    });
}

function ToolsSection() {
    const t = useT("editor");
    const mcpTools = useEditorStore((s) => s.mcpTools);
    const mcpStatus = useEditorStore((s) => s.mcpStatus);
    const isRunning = mcpStatus === "running";

    // Group tools by category
    const categories = useMemo(() => {
        const map = new Map<string, McpToolMetadata[]>();
        for (const tool of mcpTools) {
            const cat = tool.category ?? t("mcpDetail.tools.other");
            const list = map.get(cat) ?? [];
            list.push(tool);
            map.set(cat, list);
        }
        return map;
    }, [mcpTools, t]);

    return (
        <div>
            <SectionHeader
                icon={<Wrench size={14} />}
                title={t("mcpDetail.info.tools")}
                badge={mcpTools.length || undefined}
            />
            {!isRunning || mcpTools.length === 0 ? (
                <div className="rounded-lg border border-border/50 bg-card p-4 text-center">
                    <p className="text-xs text-muted-foreground/50">
                        {isRunning
                            ? t("mcpDetail.tools.empty")
                            : t("mcpDetail.tools.startHint")}
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {[...categories.entries()].map(([cat, tools]) => (
                        <ToolCategoryGroup key={cat} category={cat} tools={tools} />
                    ))}
                </div>
            )}
        </div>
    );
}

function ToolCategoryGroup({
    category,
    tools,
}: {
    category: string;
    tools: McpToolMetadata[];
}) {
    const [open, setOpen] = useState(false);

    return (
        <div className="rounded-lg border border-border/50 overflow-hidden">
            <button
                type="button"
                className="flex items-center gap-2 w-full px-3 py-2 hover:bg-muted/30 transition-colors"
                onClick={() => setOpen(!open)}
            >
                {open ? (
                    <ChevronDown
                        size={12}
                        className="text-muted-foreground/50 shrink-0"
                    />
                ) : (
                    <ChevronRight
                        size={12}
                        className="text-muted-foreground/50 shrink-0"
                    />
                )}
                <span className="text-muted-foreground/60">
                    {getCategoryIcon(category)}
                </span>
                <span className="text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider flex-1 text-left">
                    {category}
                </span>
                <span className="text-[10px] font-medium text-muted-foreground/40 tabular-nums">
                    {tools.length}
                </span>
            </button>
            {open ? (
                <div className="border-t border-border/30 divide-y divide-border/20">
                    {tools.map((tool) => (
                        <ToolRow key={tool.name} tool={tool} />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function ToolRow({ tool }: { tool: McpToolMetadata }) {
    const t = useT("editor");
    const [expanded, setExpanded] = useState(false);
    const hasParams = tool.params.length > 0;
    const ann = tool.annotations;

    return (
        <div className="hover:bg-muted/20 transition-colors">
            <button
                type="button"
                className="flex items-start gap-2.5 px-3 py-2 w-full text-left"
                onClick={() => hasParams && setExpanded(!expanded)}
            >
                <Zap size={11} className="text-primary/50 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-medium text-foreground/80 leading-tight">
                            {tool.name}
                        </span>
                        {ann?.readOnlyHint ? (
                            <span
                                className="theme-accent-chip rounded px-1 py-px text-[9px] font-medium"
                                style={getThemeAccentStyle("info")}
                            >
                                {t("mcpDetail.annotation.readOnly")}
                            </span>
                        ) : ann?.destructiveHint ? (
                            <span
                                className="theme-accent-chip rounded px-1 py-px text-[9px] font-medium"
                                style={getThemeAccentStyle("error")}
                            >
                                {t("mcpDetail.annotation.destructive")}
                            </span>
                        ) : null}
                        {hasParams ? (
                            <span className="text-[10px] font-mono text-muted-foreground/40">
                                ({t("mcpDetail.tool.paramsSummary", {
                                    required: tool.params.filter((p) => p.required).length,
                                    total: tool.params.length,
                                })})
                            </span>
                        ) : (
                            <span className="text-[10px] font-mono text-muted-foreground/30">
                                ({t("mcpDetail.tool.noParams")})
                            </span>
                        )}
                        {hasParams ? (
                            expanded ? (
                                <ChevronDown
                                    size={10}
                                    className="text-muted-foreground/30 ml-auto shrink-0"
                                />
                            ) : (
                                <ChevronRight
                                    size={10}
                                    className="text-muted-foreground/30 ml-auto shrink-0"
                                />
                            )
                        ) : null}
                    </div>
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5 leading-snug">
                        {tool.description}
                    </p>
                </div>
            </button>
            {expanded && hasParams ? (
                <div className="mx-3 mb-2 ml-8">
                    <div className="rounded-md border border-border/30 bg-muted/20 overflow-hidden">
                        <div className="px-2 py-1 border-b border-border/20">
                            <span className="text-[9px] font-semibold text-muted-foreground/50 uppercase tracking-wider">
                                {t("common.parameters")}
                            </span>
                        </div>
                        <table className="w-full text-[10px]">
                            <thead>
                                <tr className="border-b border-border/20 text-muted-foreground/50">
                                    <th className="text-left font-semibold px-2 py-1 uppercase tracking-wider">
                                        {t("common.name")}
                                    </th>
                                    <th className="text-left font-semibold px-2 py-1 uppercase tracking-wider">
                                        {t("common.type")}
                                    </th>
                                    <th className="text-left font-semibold px-2 py-1 uppercase tracking-wider w-16">
                                        {t("mcpDetail.table.req")}
                                    </th>
                                    <th className="text-left font-semibold px-2 py-1 uppercase tracking-wider">
                                        {t("common.description")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/10">
                                {tool.params.map((p) => (
                                    <tr key={p.name}>
                                        <td className="px-2 py-1 font-mono font-medium text-foreground/70">
                                            {p.name}
                                        </td>
                                        <td
                                            className="theme-accent-text px-2 py-1 font-mono"
                                            style={getThemeAccentStyle("info")}
                                        >
                                            {p.type}
                                        </td>
                                        <td className="px-2 py-1">
                                            {p.required ? (
                                                <span
                                                    className="theme-accent-text font-medium"
                                                    style={getThemeAccentStyle("warning")}
                                                >
                                                    {t("common.yes")}
                                                </span>
                                            ) : (
                                                <span className="text-muted-foreground/40">{t("common.no")}</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-1 text-muted-foreground/60">
                                            {p.description}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/* ── Resources Section ── */

function ResourcesSection() {
    const t = useT("editor");
    const mcpResources = useEditorStore((s) => s.mcpResources);
    const mcpStatus = useEditorStore((s) => s.mcpStatus);
    const isRunning = mcpStatus === "running";

    return (
        <div>
            <SectionHeader icon={<Database size={14} />} title={t("mcpDetail.info.resources")} badge={mcpResources.length || undefined} />
            {!isRunning || mcpResources.length === 0 ? (
                <div className="rounded-lg border border-border/50 bg-card p-4 text-center">
                    <p className="text-xs text-muted-foreground/50">
                        {isRunning ? t("mcpDetail.resources.empty") : t("mcpDetail.resources.startHint")}
                    </p>
                </div>
            ) : (
                <div className="rounded-lg border border-border/50 overflow-hidden divide-y divide-border/20">
                    {mcpResources.map((resource) => (
                        <ResourceRow key={resource.name} resource={resource} />
                    ))}
                </div>
            )}
        </div>
    );
}

function ResourceRow({ resource }: { resource: McpResourceMetadata }) {
    const t = useT("editor");
    return (
        <div className="flex items-start gap-2.5 px-3 py-2.5 hover:bg-muted/20 transition-colors">
            <Database size={11} className="text-primary/50 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-medium text-foreground/80 leading-tight">
                        {resource.name}
                    </span>
                    {resource.isTemplate ? (
                        <span
                            className="theme-accent-chip rounded px-1.5 py-0.5 text-[10px] font-medium"
                            style={getThemeAccentStyle("info")}
                        >
                            {t("mcpDetail.resource.template")}
                        </span>
                    ) : null}
                    {resource.mimeType ? (
                        <span className="text-[10px] font-mono text-muted-foreground/40">
                            {resource.mimeType}
                        </span>
                    ) : null}
                </div>
                <p className="text-[10px] text-muted-foreground/60 mt-0.5 leading-snug">
                    {resource.description}
                </p>
                <p className="text-[10px] font-mono text-muted-foreground/40 mt-0.5">
                    {resource.uriPattern}
                </p>
            </div>
        </div>
    );
}

/* ── Prompts Section ── */

function PromptsSection() {
    const t = useT("editor");
    const mcpPrompts = useEditorStore((s) => s.mcpPrompts);
    const mcpStatus = useEditorStore((s) => s.mcpStatus);
    const isRunning = mcpStatus === "running";

    return (
        <div>
            <SectionHeader
                icon={<BookOpen size={14} />}
                title={t("mcpDetail.info.prompts")}
                badge={mcpPrompts.length || undefined}
            />
            {!isRunning || mcpPrompts.length === 0 ? (
                <div className="rounded-lg border border-border/50 bg-card p-4 text-center">
                    <p className="text-xs text-muted-foreground/50">
                        {isRunning
                            ? t("mcpDetail.prompts.empty")
                            : t("mcpDetail.prompts.startHint")}
                    </p>
                </div>
            ) : (
                <div className="rounded-lg border border-border/50 overflow-hidden divide-y divide-border/20">
                    {mcpPrompts.map((prompt) => (
                        <PromptRow key={prompt.name} prompt={prompt} />
                    ))}
                </div>
            )}
        </div>
    );
}

function PromptRow({ prompt }: { prompt: McpPromptMetadata }) {
    const t = useT("editor");
    const [expanded, setExpanded] = useState(false);
    const hasArgs = (prompt.args?.length ?? 0) > 0;

    return (
        <div className="hover:bg-muted/20 transition-colors">
            <button
                type="button"
                className="flex items-start gap-2.5 px-3 py-2 w-full text-left"
                onClick={() => hasArgs && setExpanded(!expanded)}
            >
                <BookOpen size={11} className="text-primary/50 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-medium text-foreground/80 leading-tight">
                            {prompt.name}
                        </span>
                        {hasArgs ? (
                            <span className="text-[10px] font-mono text-muted-foreground/40">
                                ({t("common.arguments")}: {prompt.args!.length})
                            </span>
                        ) : null}
                        {hasArgs ? (
                            expanded ? (
                                <ChevronDown
                                    size={10}
                                    className="text-muted-foreground/30 ml-auto shrink-0"
                                />
                            ) : (
                                <ChevronRight
                                    size={10}
                                    className="text-muted-foreground/30 ml-auto shrink-0"
                                />
                            )
                        ) : null}
                    </div>
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5 leading-snug">
                        {prompt.description}
                    </p>
                </div>
            </button>
            {expanded && hasArgs ? (
                <div className="mx-3 mb-2 ml-8">
                    <div className="rounded-md border border-border/30 bg-muted/20 overflow-hidden">
                        <div className="px-2 py-1 border-b border-border/20">
                            <span className="text-[9px] font-semibold text-muted-foreground/50 uppercase tracking-wider">
                                {t("common.arguments")}
                            </span>
                        </div>
                        <div className="divide-y divide-border/10">
                            {prompt.args!.map((arg) => (
                                <div
                                    key={arg.name}
                                    className="flex items-center gap-3 px-2 py-1.5 text-[10px]"
                                >
                                    <span className="font-mono font-medium text-foreground/70">
                                        {arg.name}
                                    </span>
                                    {arg.required ? (
                                        <span
                                            className="theme-accent-text font-medium"
                                            style={getThemeAccentStyle("warning")}
                                        >
                                            {t("common.required")}
                                        </span>
                                    ) : (
                                        <span className="text-muted-foreground/40">{t("common.optional")}</span>
                                    )}
                                    {arg.description ? (
                                        <span className="text-muted-foreground/60">
                                            {arg.description}
                                        </span>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/* ── Request Logs Panel ── */

function RequestLogPanel({
    logs,
    selectedClientId,
    selectedClient,
}: {
    logs: McpRequestLogEntry[];
    selectedClientId: string;
    selectedClient: ClientActivityItem | null;
}) {
    const t = useT("editor");
    const mcpStatus = useEditorStore((s) => s.mcpStatus);
    const clearLogs = useEditorStore((s) => s.clearMcpRequestLogs);
    const [statusFilter, setStatusFilter] = useState<LogStatusFilter>("all");
    const [nameFilter, setNameFilter] = useState("");
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const isRunning = mcpStatus === "running";

    const scopedLogs = useMemo(() => {
        if (selectedClientId === ALL_CLIENTS_ID) return logs;
        if (selectedClientId === STATELESS_CLIENT_ID) {
            return logs.filter((log) => !log.sessionId);
        }
        return logs.filter((log) => log.sessionId === selectedClientId);
    }, [logs, selectedClientId]);

    const filteredLogs = useMemo(() => {
        let result = scopedLogs;
        if (statusFilter !== "all") {
            result = result.filter((l) => l.status === statusFilter);
        }
        if (nameFilter) {
            const lowered = nameFilter.toLowerCase();
            result = result.filter(
                (l) =>
                    (l.name ?? "").toLowerCase().includes(lowered) ||
                    l.method.toLowerCase().includes(lowered),
            );
        }
        // Show most recent first
        return [...result].reverse();
    }, [scopedLogs, statusFilter, nameFilter]);

    const toggleExpand = useCallback((id: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const statusCounts = useMemo(() => {
        const counts: Record<LogStatusFilter, number> = {
            all: scopedLogs.length,
            pending: 0,
            success: 0,
            error: 0,
        };
        for (const l of scopedLogs) {
            if (l.status === "pending") counts.pending++;
            else if (l.status === "success") counts.success++;
            else if (l.status === "error") counts.error++;
        }
        return counts;
    }, [scopedLogs]);

    const title =
        selectedClientId === ALL_CLIENTS_ID
            ? t("mcpDetail.logs.request")
            : selectedClient?.name ?? t("mcpDetail.clients.unknown");

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-card">
            <div className="p-3 border-b border-border/40 space-y-2">
                <div className="flex items-center gap-2 min-w-0">
                    <List size={14} className="text-muted-foreground shrink-0" />
                    <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                        {title}
                    </h3>
                    <span className="shrink-0 rounded bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground tabular-nums">
                        {scopedLogs.length}
                    </span>
                    <button
                        type="button"
                        onClick={clearLogs}
                        className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                        title={t("mcpDetail.clearLogs")}
                    >
                        <Trash2 size={12} />
                    </button>
                </div>

                <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
                    <div className="flex items-center rounded-md border border-border/50 overflow-hidden text-[10px]">
                        {(["all", "success", "error", "pending"] as LogStatusFilter[]).map(
                            (s) => (
                                <button
                                    key={s}
                                    type="button"
                                    className={cn(
                                        "px-2 py-1 transition-colors",
                                        statusFilter === s
                                            ? "bg-primary/10 text-primary font-medium"
                                            : "text-muted-foreground hover:text-foreground hover:bg-muted/30",
                                    )}
                                    onClick={() => setStatusFilter(s)}
                                >
                                    {logStatusLabel(s, t)}
                                    <span className="ml-1 opacity-50">{statusCounts[s]}</span>
                                </button>
                            ),
                        )}
                    </div>

                    <div className="flex items-center gap-1 min-w-0 flex-1">
                        <Filter size={11} className="text-muted-foreground/50 shrink-0" />
                        <input
                            type="text"
                            placeholder={t("mcpDetail.filterPlaceholder")}
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/40 outline-none"
                        />
                    </div>
                </div>
            </div>

            {!isRunning && logs.length === 0 ? (
                <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center">
                    <p className="text-xs text-muted-foreground/50">
                        {t("mcpDetail.logs.startHint")}
                    </p>
                </div>
            ) : filteredLogs.length === 0 ? (
                <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center">
                    <p className="text-xs text-muted-foreground/50">{t("mcpDetail.logs.empty")}</p>
                </div>
            ) : (
                <div className="min-h-0 flex-1 overflow-auto divide-y divide-border/20">
                    {filteredLogs.map((log, index) => (
                        <LogRow
                            key={log.id}
                            log={log}
                            expanded={expanded.has(log.id) || (expanded.size === 0 && index === 0)}
                            onToggle={() => toggleExpand(log.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function logStatusLabel(filter: LogStatusFilter, t: ReturnType<typeof useT>): string {
    if (filter === "success") return t("mcpDetail.logs.status.success");
    if (filter === "error") return t("mcpDetail.logs.status.error");
    if (filter === "pending") return t("mcpDetail.logs.status.pending");
    return t("common.all");
}

function LogRow({
    log,
    expanded,
    onToggle,
}: {
    log: McpRequestLogEntry;
    expanded: boolean;
    onToggle: () => void;
}) {
    const t = useT("editor");
    const statusIcon =
        log.status === "pending" ? (
            <Loader2
                size={10}
                className="theme-accent-text animate-spin"
                style={getThemeAccentStyle("warning")}
            />
        ) : log.status === "success" ? (
            <Circle
                size={8}
                className="theme-accent-text fill-current"
                style={getThemeAccentStyle("success")}
            />
        ) : (
            <Circle
                size={8}
                className="theme-accent-text fill-current"
                style={getThemeAccentStyle("error")}
            />
        );

    const time = new Date(log.timestamp);
    const timeStr = `${time.getHours().toString().padStart(2, "0")}:${time.getMinutes().toString().padStart(2, "0")}:${time.getSeconds().toString().padStart(2, "0")}`;
    const sessionLabel = log.sessionId
        ? `${log.sessionId.slice(0, 8)}...${log.sessionId.slice(-4)}`
        : t("mcpDetail.clients.stateless");
    const requestPayload = log.params ?? log.arguments;
    const hasRequestPayload =
        requestPayload != null && Object.keys(requestPayload).length > 0;
    const responsePayload =
        log.response && Object.prototype.hasOwnProperty.call(log.response, "body")
            ? log.response.body
            : log.response?.text;
    const hasResponsePayload =
        log.response != null &&
        (Object.prototype.hasOwnProperty.call(log.response, "body") ||
            typeof log.response.text === "string");
    const hasMeta =
        log.requestId != null ||
        log.response?.statusCode != null ||
        log.response?.sizeBytes != null ||
        log.response?.truncated;

    return (
        <div className="bg-card hover:bg-muted/20 transition-colors">
            <button
                type="button"
                className="flex items-center gap-2 w-full px-3 py-2 text-left"
                onClick={onToggle}
            >
                {expanded ? (
                    <ChevronDown
                        size={10}
                        className="text-muted-foreground/40 shrink-0"
                    />
                ) : (
                    <ChevronRight
                        size={10}
                        className="text-muted-foreground/40 shrink-0"
                    />
                )}
                {statusIcon}
                <span className="text-[10px] text-muted-foreground/60 font-mono tabular-nums shrink-0">
                    {timeStr}
                </span>
                <span className="text-[11px] font-mono text-muted-foreground/50 shrink-0">
                    {log.method}
                </span>
                {log.name ? (
                    <span className="text-xs font-mono font-medium text-foreground/80 truncate">
                        {log.name}
                    </span>
                ) : null}
                <span
                    className="min-w-0 truncate text-[10px] font-mono text-muted-foreground/40"
                    title={log.sessionId ?? undefined}
                >
                    {sessionLabel}
                </span>
                <span className="flex-1" />
                {log.duration != null ? (
                    <span className="text-[10px] text-muted-foreground/40 font-mono tabular-nums shrink-0">
                        {log.duration}ms
                    </span>
                ) : null}
            </button>
            {expanded ? (
                <div className="px-3 pb-3 space-y-2">
                    {hasMeta ? (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-muted/15 px-2 py-1.5 text-[10px] text-muted-foreground/60">
                            {log.requestId != null ? (
                                <span>
                                    {t("mcpDetail.logs.requestId")}{" "}
                                    <code className="font-mono text-muted-foreground/80">
                                        {String(log.requestId)}
                                    </code>
                                </span>
                            ) : null}
                            {log.response?.statusCode != null ? (
                                <span>
                                    {t("mcpDetail.logs.responseStatus")}{" "}
                                    <code className="font-mono text-muted-foreground/80">
                                        {log.response.statusCode}
                                    </code>
                                </span>
                            ) : null}
                            {log.response?.sizeBytes != null ? (
                                <span>
                                    {t("mcpDetail.logs.responseSize")}{" "}
                                    <code className="font-mono text-muted-foreground/80">
                                        {formatBytes(log.response.sizeBytes)}
                                    </code>
                                </span>
                            ) : null}
                            {log.response?.truncated ? (
                                <span>{t("mcpDetail.logs.responseTruncated")}</span>
                            ) : null}
                        </div>
                    ) : null}
                    {hasRequestPayload ? (
                        <div>
                            <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">
                                {t("mcpDetail.logs.params")}
                            </span>
                            <div className="bg-muted/20 rounded px-2 py-1.5 overflow-auto max-h-40 mt-0.5">
                                <JsonTree value={requestPayload} defaultExpandDepth={2} />
                            </div>
                        </div>
                    ) : null}
                    {log.response ? (
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">
                                    {t("mcpDetail.logs.response")}
                                </span>
                                {log.response.contentType ? (
                                    <span className="truncate text-[10px] text-muted-foreground/35">
                                        {log.response.contentType}
                                    </span>
                                ) : null}
                            </div>
                            <div className="bg-muted/20 rounded px-2 py-1.5 overflow-auto max-h-56 mt-0.5">
                                {hasResponsePayload ? (
                                    typeof responsePayload === "string" ? (
                                        <pre className="whitespace-pre-wrap break-all text-[10px] font-mono text-muted-foreground/80">
                                            {responsePayload}
                                        </pre>
                                    ) : (
                                        <JsonTree value={responsePayload} defaultExpandDepth={2} />
                                    )
                                ) : (
                                    <span className="text-[10px] text-muted-foreground/50">
                                        {t("mcpDetail.logs.noResponse")}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : null}
                    {!hasRequestPayload && !log.response && !log.error ? (
                        <div className="rounded bg-muted/15 px-2 py-1.5 text-[10px] text-muted-foreground/50">
                            {t("mcpDetail.logs.noDetails")}
                        </div>
                    ) : null}
                    {log.error ? (
                        <div>
                            <span
                                className="theme-accent-text text-[10px] uppercase tracking-wider"
                                style={getThemeAccentStyle("error")}
                            >
                                {t("common.error")}
                            </span>
                            <pre
                                className="theme-accent-banner mt-0.5 max-h-20 overflow-auto rounded px-2 py-1.5 text-[10px] font-mono"
                                style={getThemeAccentStyle("error")}
                            >
                                {log.error}
                            </pre>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
